import { DeleteOutlined } from '@ant-design/icons'
import { css } from '@emotion/react'
import { Button, Modal } from 'antd'
import { FC } from 'react'
import { useSelector } from 'react-redux'
import { Spinner } from 'src/features/reportExport/list/components/Spinner'
import { useAddMultipleChoices } from 'src/features/templateEdit/responseMultipleChoices/hooks/useAddMultipleChoices'
import { useDeleteMultipleChoices } from 'src/features/templateEdit/responseMultipleChoices/hooks/useDeleteMultipleChoices'
import { useUpdateMultipleChoices } from 'src/features/templateEdit/responseMultipleChoices/hooks/useUpdateMultipleChoices'
import { useSourceTemplateIdParam } from 'src/features/templateEdit/useSourceTemplateIdParam'
import { RootState } from 'src/state/store'
import DeleteConfirmDialog from 'src/views/components/dialogs/DeleteConfirmDialog'
import { useIsMultipleChoiceGroupInUse } from '../../hooks/useIsMultipleChoiceGroupInUse'
import {
  ButtonGroup,
  ButtonGroupRow,
  ConfirmModalContentTemplates,
  ConfirmModalContentTemplatesList,
  ConfirmModalContentWrapper,
  FooterButton,
} from '../../style/MultipleChoiceDrawer.styled'
import AlertModalContent from '../AlertModalContent/AlertModalContent'
import {
  createMultipleChoiceButtonsCommon,
  MultipleChoiceButtonsRowProps,
} from './multipleChoiceButtonsCommon'

/**
 * 従来の選択肢（他のひな形でも使用中）用のボタン行コンポーネント
 * - TanStack Query を使用
 * - relatedTemplates による他のひな形への影響チェックあり
 */
const CommonMultipleChoiceButtonsRow: FC<MultipleChoiceButtonsRowProps> = ({
  onClose,
  multipleChoicesState: [multipleChoices, setMultipleChoices],
  multipleChoiceGroupForUpdate,
  relatedTemplates,
  isFetchingRelatedTemplates,
  isErrorRelatedTemplates,
}) => {
  const isInUse = useIsMultipleChoiceGroupInUse(multipleChoiceGroupForUpdate)
  const templateId = useSourceTemplateIdParam()
  const templateName = useSelector(
    (state: RootState) => state.templatesState.editTemplate?.name || '',
  )
  const { addMultipleChoices, isPendingAddMultipleChoices } =
    useAddMultipleChoices()
  const { updateMultipleChoices, isPendingUpdateMultipleChoices } =
    useUpdateMultipleChoices()
  const { deleteMultipleChoices, isPendingDeleteMultipleChoices } =
    useDeleteMultipleChoices()

  const { handleCloseDrawer, getFilteredMultipleChoices } =
    createMultipleChoiceButtonsCommon(
      multipleChoices,
      setMultipleChoices,
      onClose,
    )

  const isNew = multipleChoiceGroupForUpdate === undefined

  const confirmModal = () =>
    Modal.confirm({
      onOk: (close: () => void) => {
        const filteredMultipleChoices = getFilteredMultipleChoices()

        if (filteredMultipleChoices.length > 0) {
          if (multipleChoiceGroupForUpdate) {
            updateMultipleChoices(
              {
                multipleChoiceId: multipleChoiceGroupForUpdate.id ?? 0,
                multipleChoices: filteredMultipleChoices,
              },
              () => {
                close()
                handleCloseDrawer()
              },
            )
          } else {
            addMultipleChoices(filteredMultipleChoices, () => {
              close()
              handleCloseDrawer()
            })
          }
        } else {
          close()
          handleCloseDrawer()
        }
      },
      title: '選択肢を保存してよろしいですか？',
      content: !relatedTemplates?.length ? null : (
        <ConfirmModalContentWrapper>
          この選択肢の変更は、以下のひな形にも影響する可能性があります。
          <ConfirmModalContentTemplates>
            <ConfirmModalContentTemplatesList>
              {relatedTemplates.map(template => (
                <li key={template.id}>{template.name}</li>
              ))}
            </ConfirmModalContentTemplatesList>
          </ConfirmModalContentTemplates>
        </ConfirmModalContentWrapper>
      ),
      okText: '保存する',
      cancelText: 'キャンセル',
    })

  const confirmDeleteModal = () => {
    const deletingMultipleChoiceGroupId = multipleChoiceGroupForUpdate?.id
    if (!deletingMultipleChoiceGroupId) return

    if (relatedTemplates?.length || isInUse) {
      Modal.error({
        title: '以下のひな形で使用されているため削除できません。',
        content: (
          <AlertModalContent
            relatedTemplates={relatedTemplates ?? null}
            templateId={templateId}
            templateName={templateName}
            isInUse={isInUse}
          />
        ),
      })
      return
    }

    DeleteConfirmDialog({
      title: '選択肢を削除してよろしいですか？',
      onOk: () => {
        deleteMultipleChoices(deletingMultipleChoiceGroupId, () => {
          handleCloseDrawer()
        })
      },
    })
  }

  return (
    <>
      <ButtonGroupRow>
        <ButtonGroup>
          {!isNew && (
            <Button
              danger
              disabled={
                isFetchingRelatedTemplates ||
                isErrorRelatedTemplates ||
                isPendingDeleteMultipleChoices
              }
              type="link"
              icon={<DeleteOutlined />}
              onClick={confirmDeleteModal}
            >
              選択肢を削除
            </Button>
          )}
        </ButtonGroup>
        <ButtonGroup>
          <FooterButton
            type="link"
            onClick={() => {
              onClose()
              setMultipleChoices({})
            }}
          >
            キャンセル
          </FooterButton>
          <FooterButton
            disabled={
              isFetchingRelatedTemplates ||
              isErrorRelatedTemplates ||
              isPendingAddMultipleChoices ||
              isPendingUpdateMultipleChoices ||
              isPendingDeleteMultipleChoices
            }
            type="primary"
            onClick={confirmModal}
          >
            保存
          </FooterButton>
        </ButtonGroup>
      </ButtonGroupRow>

      {(isFetchingRelatedTemplates ||
        isPendingAddMultipleChoices ||
        isPendingUpdateMultipleChoices ||
        isPendingDeleteMultipleChoices) && (
        <div css={styles.spinner}>
          <Spinner />
        </div>
      )}
    </>
  )
}

const styles = {
  spinner: css`
    margin: 16px;
  `,
}

export default CommonMultipleChoiceButtonsRow
